import { useState, useEffect, useCallback } from 'react'; // Adicionado useCallback
import { FileText, Calendar, Users, Search, Download } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { supabase } from '../../config/supabase'; // Importação do Supabase
import { useAuth } from '../../context/AuthContext';
// import { getMedicalRecords } from '../../services/MedicalRecordService'; // Temporariamente removido
import { toast } from 'react-toastify';
import { NotificationBell } from '../../components/common/NotificationBell';
import { AppointmentStatusModal } from '../../components/common/AppointmentStatusModal';
import { PendingAppointment } from '../../services/AppointmentStatusService';
import { MedicalRecord, User as SupabaseUser } from '../../types/supabase'; // Novos tipos
import { getMedicalRecordDownloadUrl } from '../../utils/medicalRecordFiles';
import { isManualUploadedMedicalRecord, isMedicalRecordInvalidated } from '../../utils/medicalRecordStatus';
import { matchesPatientSearch, normalizeSearchText } from '../../utils/patientSearch';

// TODO: Mover esta função para MedicalRecordService.ts após migração
const fetchMedicalRecords = async (userId: string, clientId: string, userRole: string): Promise<MedicalRecord[]> => {
  if (!userId || !clientId) {
    console.error('UserId ou ClientId não fornecidos para buscar registros médicos.');
    return [];
  }

  try {
    const { data, error } = await supabase
      .from("medical_records")
      .select(
        `
        recordId,
        clientId,
        patientId,
        doctorId,
        recordType,
        recordDate,
        recordTitle,
        recordDescription,
        recordAttachments,
        recordCreatedAt,
        recordUpdatedAt,
        patients(patientFullName),
        users(userDisplayName)
        `
      )
      .eq("doctorId", userId) // Filtrar pelos registros do doutor logado
      .eq("clientId", clientId) // Filtrar pelo cliente atual
      .order("recordDate", { ascending: false });

    if (error) {
      throw error;
    }

    const mappedRecords: MedicalRecord[] = data
      .filter((record: any) =>
        !isMedicalRecordInvalidated(record.recordType, record.recordAttachments) &&
        isManualUploadedMedicalRecord(record.recordAttachments)
      )
      .map((record: any) => ({
        recordId: record.recordId,
        clientId: record.clientId,
        patientId: record.patientId,
        doctorId: record.doctorId,
        recordType: record.recordType,
        recordDate: record.recordDate,
        recordTitle: record.recordTitle,
        recordDescription: record.recordDescription,
        recordAttachments: record.recordAttachments,
        recordCreatedAt: record.recordCreatedAt,
        recordUpdatedAt: record.recordUpdatedAt,
        patientFullName: record.patients?.patientFullName || record.patients?.[0]?.patientFullName || undefined, // Ajustado para ser opcional
        doctorFullName: record.users?.userDisplayName || record.users?.[0]?.userDisplayName || undefined,   // Ajustado para ser opcional
        // Campos legados mapeados para a nova estrutura, garantindo tipagem correta
        id: record.recordId, // Manter id para compatibilidade do React key
        name: record.recordTitle,
        url: Array.isArray(record.recordAttachments)
          ? record.recordAttachments[0]?.url || record.recordAttachments[0]?.fileUrl || ''
          : '',
        uploadDate: record.recordDate, // Agora é string
        patientName: record.patients?.patientFullName || record.patients?.[0]?.patientFullName || undefined,
        doctorName: record.users?.userDisplayName || record.users?.[0]?.userDisplayName || undefined,
        metadata: record.recordAttachments,
      }));
    
    return mappedRecords;
  } catch (error: any) {
    console.error('Erro ao buscar registros médicos:', error.message);
    throw error;
  }
};

const DoctorDashboard = () => {
  const { user: authUser } = useAuth(); // Renomear user do useAuth para authUser
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<MedicalRecord[]>([]);
  const [userData, setUserData] = useState<SupabaseUser | null>(null); // Inicializar como null, será preenchido pelo fetch
  const [selectedAppointment, setSelectedAppointment] = useState<PendingAppointment | null>(null);

  useEffect(() => {
    if (!authUser?.userId) return; // Usar userId do AuthContext

    const fetchUserData = async () => {
      try {
        // Select na tabela users e join com doctors e user_clients
        const { data: fetchedData, error } = await supabase
          .from("users")
          .select(
            `
              userId,
              userEmail,
              userDisplayName,
              userPhoneNumber,
              userPhotoUrl,
              userAssignedCalendarName,
              firebase_uid,
              current_client_id,
              user_clients(userRole),
              doctors(doctorId: doctorId, doctorCrefito, doctorCrm, doctorSpecialties)
            `
          )
          .eq("userId", authUser.userId) // userId na tabela users do Supabase
          .single();

        if (error) {
          throw error;
        }

        if (fetchedData) {
          // user_clients pode ser um array de user_clients_row, então pegamos o primeiro
          const userRoleData = (Array.isArray(fetchedData.user_clients) ? fetchedData.user_clients[0] : fetchedData.user_clients) as { userRole: string } | null | undefined;

          const mappedUserData: SupabaseUser = {
            userId: fetchedData.userId,
            userEmail: fetchedData.userEmail || null,
            userDisplayName: fetchedData.userDisplayName || null,
            userPhoneNumber: fetchedData.userPhoneNumber || null, // Tipagem corrigida para null
            userPhotoUrl: fetchedData.userPhotoUrl || null, // Tipagem corrigida para null
            userAssignedCalendarName: fetchedData.userAssignedCalendarName || null, // Tipagem corrigida para null
            userFirebase_uid: fetchedData.firebase_uid || null, // Tipagem corrigida para null
            current_client_id: fetchedData.current_client_id || null,
            userRole: userRoleData?.userRole || null, 
            
            // Dados do doutor
            doctorId: (fetchedData.doctors as any)?.doctorId || null, // Tipagem corrigida para null
            crefito: (fetchedData.doctors as any)?.doctorCrefito || null, // Tipagem corrigida para null
            crm: (fetchedData.doctors as any)?.doctorCrm || null, // Tipagem corrigida para null
            specialties: (fetchedData.doctors as any)?.doctorSpecialties || null, // Tipagem corrigida para null
          };
          setUserData(mappedUserData);
        }

      } catch (error: any) {
        console.error("Erro ao buscar dados do usuário:", error.message);
        toast.error("Erro ao carregar dados do usuário");
      }
    };

    fetchUserData();

  }, [authUser?.userId]); // Dependência do authUser.userId


  useEffect(() => {
    const loadRecords = async () => {
      if (!authUser?.userId || !authUser?.current_client_id) return; // user.userId e clientId do Supabase
      
      try {
        setIsLoading(true);
        // Chamar o novo MedicalRecordService baseado no Supabase
        const fetchedRecords = await fetchMedicalRecords(authUser.userId, authUser.current_client_id, authUser.userRole || 'defaultRole'); // Substitua 'defaultRole' por um valor padrão apropriado, se necessário.
        setRecords(fetchedRecords);
      } catch (error) {
        console.error("Error loading records:", error);
        toast.error("Erro ao carregar documentos");
      } finally {
        setIsLoading(false);
      }
    };

    loadRecords();
  }, [authUser?.userId, authUser?.current_client_id, authUser?.userRole]); // Dependências atualizadas

  const filteredRecords = records.filter(record => {
    const normalizedSearch = normalizeSearchText(searchTerm);
    return (
      matchesPatientSearch(searchTerm, record.patientFullName) ||
      normalizeSearchText(record.recordTitle).includes(normalizedSearch)
    );
  });

  const handleDownload = async (record: MedicalRecord) => {
    try {
      const downloadUrl = await getMedicalRecordDownloadUrl(record.recordAttachments);
      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Erro ao baixar documento:', error);
      toast.error('URL do documento não disponível.');
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Calendar className="w-6 h-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">Documentos Recentes</h2>
              </div>
              <p className="text-gray-600">{records.length} documentos no total</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <Users className="w-6 h-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">Pacientes</h2>
              </div>
              <p className="text-gray-600">
                {new Set(records.map(r => r.patientId)).size} pacientes com documentos
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center mb-4">
                <FileText className="w-6 h-6 text-primary-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-800">Últimos Uploads</h2>
              </div>
              <p className="text-gray-600">
                {records.filter(r => {
                  const today = new Date();
                  const recordDate = new Date(r.recordDate); // Usar recordDate
                  return recordDate.getMonth() === today.getMonth();
                }).length} uploads este mês
              </p>
            </div>
          </div>
        );
      case 'exams':
        return (
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar por paciente ou tipo de documento..."
                    className="pl-10 w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center p-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documento
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paciente
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ações
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredRecords.map((record) => (
                      <tr key={record.recordId} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <FileText className="w-5 h-5 text-gray-400 mr-2" />
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {record.recordTitle}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{record.patientFullName}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {format(new Date(record.recordDate), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => void handleDownload(record)}
                            className="text-primary-600 hover:text-primary-900 inline-flex items-center"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Baixar
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="revahub-app min-h-screen bg-gray-100">
      <div className="border-b border-slate-200 bg-white py-5">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="mb-1 text-xl font-bold text-slate-900 sm:text-2xl">
                Olá, Dr(a). {userData?.userDisplayName}
              </h1>
              <p className="hidden text-sm text-slate-600 sm:block">
                Bem-vindo(a) ao seu painel médico. Aqui você pode gerenciar seus pacientes e documentos.
              </p>
            </div>
            {userData?.userDisplayName && (
              <NotificationBell
                professionalName={userData.userDisplayName}
                userRole={userData.userRole || 'defaultRole'} // Substitua 'defaultRole' por um valor padrão apropriado
                userId={userData.userId} // Passar o ID do usuário do Supabase
                onResolveAppointment={(appointment) => setSelectedAppointment(appointment)}
              />
            )}
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Visão Geral
            </button>
            <button
              onClick={() => setActiveTab('exams')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'exams'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Documentos
            </button>
          </nav>
        </div>

        {renderTabContent()}
      </div>

      {selectedAppointment && authUser && (
        <AppointmentStatusModal
          appointment={selectedAppointment}
          userId={authUser.userId} // Usar o ID do usuário do Supabase
          onClose={() => setSelectedAppointment(null)}
          onResolved={() => {
            setSelectedAppointment(null);
          }}
        />
      )}
    </div>
  );
};

export default DoctorDashboard;
